//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { getSortLabel } from 'app/utils/gaUtils';
import searchFilterSetTemplate from '@legacy-components/filters/search-filter-set.html';

(function () {
  const curalate = angular.module('curalate');

  /**
   * Outermost component for a search filter. This component receives updates from children search-filter components and will send these updates as a
   * single object corresponding to the provided set name to the SearchFilterService.
   * @param {string} name - used as the set name when this component registers as a new set and sends updates to the SearchFilterService
   * @param {object} state - the initial state for the filter set. This state is propagated down to all child controls as their initial state
   * @param {boolean} isSetLoadable - specifies whether the filter set has components that need time to load, so we can display filter loading state.
   */
  curalate.component('searchFilterSet', {
    bindings: {
      name: '@',
      state: '<',
      excludedSummaryKeys: '<',
      isSetLoadable: '<',
      lockedKeys: '<',
    },
    template: searchFilterSetTemplate,
    transclude: true,
    controller: [
      'rx',
      'SearchFilterService',
      'SearchFilterUpdates',
      'TrackingService',
      'SEARCH_FILTERS',
      function (
        rx,
        SearchFilterService,
        SearchFilterUpdates,
        TrackingService,
        SEARCH_FILTERS,
      ) {
        const self = this;
        let filters = {};
        const filterValueStream = new rx.Subject();
        const categoryName = `${TrackingService.CATEGORY_FILTER_SET}.${self.name}`;
        const hasSeenInitialUpdateForFilterKey = {};
        let updateWatchers = [];

        const executeFilterFunctions = function (updateKey, updateValue) {
          _(updateWatchers).each((updateWatcher) => {
            filters = updateWatcher(updateKey, updateValue, filters);
          });
        };

        // If this search filter set includes the most on brand sorting option
        // send a GA event with the current sorting option.
        // This is used to compare most on-brand sort usage in comparison to other sorting methods.
        const trackOnBrandSort = function (sortOrder) {
          // Dropdown locations that include the on-brand option:
          const isMediaLibrarySort =
            self.name === SEARCH_FILTERS.COLLECTION_CONTENT_FILTERS_SORT;
          const isStreamSort =
            self.name === SEARCH_FILTERS.STREAMS_FILTERS_SORT;
          const isInstagramSort =
            self.name === SEARCH_FILTERS.CONTENT_SOCIAL_SEARCH_FILTERS;

          const sortIncludesOnBrandOption =
            isMediaLibrarySort || isStreamSort || isInstagramSort;

          const gaLabel = getSortLabel(sortOrder);

          if (sortIncludesOnBrandOption && gaLabel) {
            TrackingService.recordEvent(
              TrackingService.CATEGORY_DROPDOWN_WITH_ON_BRAND,
              TrackingService.ACTION_DROPDOWN_WITH_ON_BRAND_SORT,
              gaLabel,
            );
          }
        };

        const trackUpdate = function (filterKey, updateObj) {
          let trackingAction;
          let trackingLabel;

          TrackingService.recordEvent(
            categoryName,
            TrackingService.ACTION_UPDATE_FILTER,
            filterKey,
          );

          _(updateObj).each((value, key) => {
            trackingAction =
              SearchFilterService.searchFilterUpdateToTrackingAction(key);
            trackingLabel = _.isObject(value)
              ? _(value).map((o) => _(o).safeGet('value') || o)
              : value;
            if (trackingAction) {
              TrackingService.recordEvent(
                categoryName,
                trackingAction,
                `${filterKey}:${trackingLabel.toString()}`,
              );
            }

            const isSort = filterKey === 'sort' || filterKey === 'orderBy';
            const isSetFilterAction = trackingAction === 'SetFilter';

            if (isSort && isSetFilterAction) {
              trackOnBrandSort(trackingLabel.toString());
            }
          });
        };

        // Keeps record of search-filters currently loading.
        let loadingState = [];
        let isLoading = true;

        // Streams loading state updates as boolean to subscribers.
        this.loadingStateObservable = new rx.Subject();

        const updateService = function () {
          SearchFilterService.updateSet(self.name, filters);
        };

        this.updateFilter = function (filterKey, filterValue, changeSet) {
          filters = SearchFilterService.getRegisteredSet(self.name).params;
          filters[filterKey] = filterValue;
          executeFilterFunctions(filterKey, filterValue);
          updateService();

          // Check that we've already seen the initial update for this filter key on initialization, if this
          // is a subsequent update, log appropriate GA events.
          if (
            hasSeenInitialUpdateForFilterKey[filterKey] &&
            !_(changeSet).safeGet(SearchFilterUpdates.INIT)
          ) {
            trackUpdate(filterKey, changeSet);
          }
          hasSeenInitialUpdateForFilterKey[filterKey] = true;
        };

        this.sendLoadingState = function (state) {
          isLoading = state.length !== 0;
          self.loadingStateObservable.next(isLoading);
        };

        this.setLoadingState = function (filterKey) {
          if (_(loadingState).contains(filterKey)) {
            throw new Error('Filter key already exists in loading state.');
          } else {
            loadingState.push(filterKey);
            self.sendLoadingState(loadingState);
          }
        };

        this.completeLoadingState = function (filterKey) {
          loadingState = _(loadingState).without(filterKey);
          self.sendLoadingState(loadingState);
        };

        this.isKeyLocked = function (key) {
          return _(self.lockedKeys).contains(key);
        };

        this.getFilterState = function () {
          return filters;
        };

        this.getFilterValueStream = function () {
          return filterValueStream;
        };

        this.registerUpdateWatcher = function (updateWatcher) {
          updateWatchers.push(updateWatcher);
        };

        this.registerFilterKeyAlias = function (filterKey, alias) {
          return SearchFilterService.registerFilterKeyAlias(
            self.name,
            filterKey,
            alias,
          );
        };

        this.registerFilterOptionAlias = function (
          filterKey,
          optionKey,
          alias,
        ) {
          return SearchFilterService.registerFilterOptionAlias(
            self.name,
            filterKey,
            optionKey,
            alias,
          );
        };

        this.registerFilterComparator = function (filterKey, comparatorFn) {
          return SearchFilterService.registerFilterComparator(
            self.name,
            filterKey,
            comparatorFn,
          );
        };

        this.$onInit = function () {
          if (!self.isSetLoadable) {
            isLoading = false;
          }
          SearchFilterService.registerNewFilterSet(self.name, self.state);
          SearchFilterService.setExcludedKeys(
            self.name,
            self.excludedSummaryKeys,
          );
          SearchFilterService.registerLockedKeys(self.name, self.lockedKeys);
          filters = self.state;
          updateService();

          SearchFilterService.getObservableForFilterSet(self.name).subscribe(
            (setUpdate) => {
              filterValueStream.next(setUpdate);
            },
          );

          self.loadingStateObservable.next(isLoading);
        };

        this.$onDestroy = function () {
          updateWatchers = [];
          SearchFilterService.unregisterFilterSet(self.name);
        };
      },
    ],
  });
})();
